/**
 * Pilot Ops Portal
 * Admin dashboard for reviewing driver/PUDO applications
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import api from "../../lib/api";
import {
  Users,
  Store,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Loader2,
  Filter,
  Mail,
  Phone
} from "lucide-react";

const PilotOpsPortal = () => {
  const [opsToken, setOpsToken] = useState(localStorage.getItem("ops_token") || "");
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("drivers"); // drivers, pudo
  const [filter, setFilter] = useState("pending");
  
  const [driverApps, setDriverApps] = useState([]);
  const [pudoApps, setPudoApps] = useState([]);

  // Check token on mount
  useEffect(() => {
    if (opsToken) {
      fetchApplications();
    }
  }, [opsToken]);

  const handleLogin = () => {
    if (!opsToken.trim()) {
      toast.error("Please enter your ops token");
      return;
    }
    localStorage.setItem("ops_token", opsToken);
    setAuthenticated(true);
    fetchApplications();
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const headers = { "X-OPS-TOKEN": opsToken };
      
      const [driverRes, pudoRes] = await Promise.all([
        api.get("/ops/driver-applications", { headers }),
        api.get("/ops/pudo-applications", { headers })
      ]);
      
      if (driverRes.data?.ok) {
        setDriverApps(driverRes.data.data || []);
      }
      if (pudoRes.data?.ok) {
        setPudoApps(pudoRes.data.data || []);
      }
      setAuthenticated(true);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error("Invalid ops token");
        setAuthenticated(false);
      } else {
        toast.error("Failed to fetch applications");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (appId, action, type) => {
    setLoading(true);
    try {
      const endpoint = action === "approve" ? `/ops/applications/${appId}/approve` : `/ops/applications/${appId}/reject`;
      const response = await api.post(endpoint, { type }, {
        headers: { "X-OPS-TOKEN": opsToken }
      });
      
      if (response.data?.ok) {
        toast.success(`Application ${action}d successfully`);
        fetchApplications();
      } else {
        throw new Error(response.data?.error?.message || "Action failed");
      }
    } catch (err) {
      toast.error(err.message || "Action failed");
    } finally {
      setLoading(false);
    }
  };

  const filteredDriverApps = driverApps.filter(app => filter === "all" || app.status === filter);
  const filteredPudoApps = pudoApps.filter(app => filter === "all" || app.status === filter);

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <span className="px-2 py-1 bg-amber-500/10 text-amber-500 rounded-full text-xs font-medium flex items-center gap-1"><Clock className="w-3 h-3" /> Pending</span>;
      case "approved":
        return <span className="px-2 py-1 bg-emerald-500/10 text-emerald-500 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Approved</span>;
      case "rejected":
        return <span className="px-2 py-1 bg-red-500/10 text-red-500 rounded-full text-xs font-medium flex items-center gap-1"><XCircle className="w-3 h-3" /> Rejected</span>;
      default:
        return <span className="px-2 py-1 bg-slate-500/10 text-slate-500 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-sky-500/15 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-sky-500" />
            </div>
            <h1 className="text-2xl font-bold text-white">Ops Dashboard</h1>
            <p className="text-slate-400 text-sm mt-2">Pilot Application Management — Go Liantiao</p>
          </div>
          
          <div className="mb-4">
            <label className="text-slate-400 text-sm block mb-2">Ops Token</label>
            <Input
              type="password"
              value={opsToken}
              onChange={(e) => setOpsToken(e.target.value)}
              placeholder="Enter your ops token"
              className="bg-slate-950 border-slate-700"
              data-testid="ops-token-input"
            />
          </div>
          
          <Button
            onClick={handleLogin}
            className="w-full bg-sky-600 hover:bg-sky-700"
            data-testid="ops-login-btn"
          >
            Access Dashboard
          </Button>
          
          <div className="mt-6 text-center">
            <Link to="/pilot" className="text-sky-500 hover:underline text-sm">
              ← Back to Public Site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/pilot" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center text-lg">
                🚚
              </div>
              <span className="text-lg font-bold text-white">Go Liantiao</span>
            </Link>
            <span className="text-sky-500 font-medium">Ops Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/pilot" className="text-slate-400 hover:text-white text-sm">
              Public Site
            </Link>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchApplications}
              className="border-slate-700"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 px-6">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <div className="text-slate-400 text-sm mb-1">Total Drivers</div>
            <div className="text-2xl font-bold text-white">{driverApps.length}</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <div className="text-slate-400 text-sm mb-1">Pending Drivers</div>
            <div className="text-2xl font-bold text-amber-500">{driverApps.filter(a => a.status === "pending").length}</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <div className="text-slate-400 text-sm mb-1">Total PUDOs</div>
            <div className="text-2xl font-bold text-white">{pudoApps.length}</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
            <div className="text-slate-400 text-sm mb-1">Pending PUDOs</div>
            <div className="text-2xl font-bold text-amber-500">{pudoApps.filter(a => a.status === "pending").length}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab("drivers")}
            className={`px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-all ${
              activeTab === "drivers" 
                ? "bg-purple-600 text-white" 
                : "bg-slate-900 border border-slate-800 text-slate-400 hover:border-purple-500"
            }`}
            data-testid="tab-drivers"
          >
            <Users className="w-4 h-4" /> Driver Applications ({driverApps.length})
          </button>
          <button
            onClick={() => setActiveTab("pudo")}
            className={`px-6 py-3 rounded-xl font-medium flex items-center gap-2 transition-all ${
              activeTab === "pudo" 
                ? "bg-sky-600 text-white" 
                : "bg-slate-900 border border-slate-800 text-slate-400 hover:border-sky-500"
            }`}
            data-testid="tab-pudo"
          >
            <Store className="w-4 h-4" /> PUDO Applications ({pudoApps.length})
          </button>
          
          {/* Filter */}
          <div className="ml-auto flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm"
              data-testid="filter-select"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Applications List */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-sky-500" />
              <p className="text-slate-400 mt-4">Loading applications...</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-800">
                <tr>
                  <th className="text-left px-6 py-4 text-slate-400 text-sm font-medium">Applicant</th>
                  <th className="text-left px-6 py-4 text-slate-400 text-sm font-medium">Contact</th>
                  <th className="text-left px-6 py-4 text-slate-400 text-sm font-medium">Details</th>
                  <th className="text-left px-6 py-4 text-slate-400 text-sm font-medium">Status</th>
                  <th className="text-left px-6 py-4 text-slate-400 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(activeTab === "drivers" ? filteredDriverApps : filteredPudoApps).map((app) => (
                  <tr key={app._id || app.id} className="border-t border-slate-800 hover:bg-slate-800/50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-white">{app.full_name || app.business_name || "N/A"}</div>
                      <div className="text-slate-500 text-sm">
                        {activeTab === "drivers" 
                          ? `${app.vehicle_type || "N/A"} • ${app.parish || "N/A"}`
                          : app.address || "N/A"
                        }
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-slate-300 text-sm mb-1">
                        <Mail className="w-3 h-3" /> {app.email || "N/A"}
                      </div>
                      <div className="flex items-center gap-2 text-slate-300 text-sm">
                        <Phone className="w-3 h-3" /> {app.phone || "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-400 text-sm">
                      {activeTab === "drivers" ? (
                        <>
                          <div>License: {app.license_number || "N/A"}</div>
                          <div>Exp: {app.experience_years || "0"} years</div>
                        </>
                      ) : (
                        <>
                          <div>Type: {app.business_type || "N/A"}</div>
                          <div>Hours: {app.operating_hours || "N/A"}</div>
                        </>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(app.status)}
                    </td>
                    <td className="px-6 py-4">
                      {app.status === "pending" && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleAction(app._id || app.id, "approve", activeTab === "drivers" ? "driver" : "pudo")}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`approve-${app._id || app.id}`}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleAction(app._id || app.id, "reject", activeTab === "drivers" ? "driver" : "pudo")}
                            className="border-red-500 text-red-500 hover:bg-red-500/10"
                            data-testid={`reject-${app._id || app.id}`}
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {(activeTab === "drivers" ? filteredDriverApps : filteredPudoApps).length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                      No {filter === "all" ? "" : filter} applications found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </main>

      <footer className="py-6 text-center text-slate-500 text-sm border-t border-slate-800">
        © 2026 Liantiao — Ops Portal (Pilot Environment)
      </footer>
    </div>
  );
};

export default PilotOpsPortal;
